/**
 * Dr. Salus - Subscribers Page
 *
 * Aba "Assinantes": stats, lista com engajamento + menu de ações
 *   (Editar / Conceder N meses / Bloquear acesso / Excluir).
 * Aba "Whitelist": números de teste que sempre passam o gating do agente.
 *
 * Mudanças aditivas: o modal de engagement existente fica intacto.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Loader2, AlertCircle, Check, X,
  RefreshCw, Copy, ExternalLink, Search,
  CheckCircle, AlertTriangle, XCircle, Clock,
  Bell, BellOff, MoreHorizontal, Edit2, Trash2,
  ShieldCheck, ShieldOff, CalendarPlus, ListChecks,
  Flame, Trophy,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import drSalusService from '../../services/dr-salus/drSalusService';
import templateService from '../../services/global/templateService';

const STATUS_CONFIG = {
  active:   { label: 'Ativo',       bg: '#22c55e20', color: '#22c55e', Icon: CheckCircle },
  overdue:  { label: 'Inadimplente', bg: '#f59e0b20', color: '#f59e0b', Icon: AlertTriangle },
  inactive: { label: 'Inativo',     bg: '#6b728020', color: '#6b7280', Icon: XCircle },
  canceled: { label: 'Cancelado',   bg: '#ef444420', color: '#ef4444', Icon: XCircle },
  pending:  { label: 'Pendente',    bg: '#3b82f620', color: '#3b82f6', Icon: Clock },
  engagement_only: { label: 'Sem assinatura', bg: '#a3a3a320', color: '#737373', Icon: AlertCircle },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, backgroundColor: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
};

const EMPTY_FORM = { plan_id: '', subscriber_name: '', subscriber_phone: '', subscriber_cpf: '', subscriber_email: '' };

export default function SubscribersPage() {
  const { colors } = useTheme();

  // Tabs
  const [activeTab, setActiveTab] = useState('subscribers');

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [includeEngagementOnly, setIncludeEngagementOnly] = useState(false);

  // Create / engagement state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [engagementSettings, setEngagementSettings] = useState(null);
  const [engagementModal, setEngagementModal] = useState(null);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [engagementForm, setEngagementForm] = useState({});
  const [savingEngagement, setSavingEngagement] = useState(false);

  // Action menu / modals
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [grantModal, setGrantModal] = useState(null);

  // Desafio Intestino Blindado: {phone: {status, dia_atual, total_days, ...}}
  const [desafioMap, setDesafioMap] = useState({});

  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [showWhitelistForm, setShowWhitelistForm] = useState(false);
  const [whitelistForm, setWhitelistForm] = useState({ phone: '', reason: '' });
  const [savingWhitelist, setSavingWhitelist] = useState(false);

  // ============================================
  // LOADERS
  // ============================================

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subsData, plansData, engSettings, tplData, statsData, desafioData] = await Promise.all([
        drSalusService.listSubscriptions(statusFilter || null, includeEngagementOnly),
        drSalusService.listPlans(),
        drSalusService.getEngagementSettings().catch(() => null),
        templateService.list().catch(() => ({ templates: [] })),
        drSalusService.getStats().catch(() => null),
        drSalusService.getDesafioParticipants().catch(() => ({ participants: {} })),
      ]);
      setSubscribers(subsData.subscribers || []);
      setPlans((plansData.plans || []).filter(p => p.active));
      setEngagementSettings(engSettings);
      setApprovedTemplates((tplData.templates || []).filter(t => t.status === 'APPROVED'));
      setStats(statsData);
      setDesafioMap(desafioData?.participants || {});
    } catch {
      setError('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, includeEngagementOnly]);

  const loadWhitelist = useCallback(async () => {
    setLoadingWhitelist(true);
    try {
      const data = await drSalusService.listWhitelist();
      setWhitelist(data.whitelist || []);
    } catch {
      setError('Erro ao carregar whitelist.');
    } finally {
      setLoadingWhitelist(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 'whitelist') loadWhitelist(); }, [activeTab, loadWhitelist]);

  // Close menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (openMenuId && !e.target.closest('.row-action-menu') && !e.target.closest('.row-action-trigger')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [openMenuId]);

  // ============================================
  // ENGAGEMENT (existing — unchanged)
  // ============================================

  const openEngagementModal = useCallback(async (sub) => {
    try {
      const state = await drSalusService.getEngagement(sub.phone);
      setEngagementForm({
        max_consecutive_unanswered: state.max_consecutive_unanswered ?? '',
        reengagement_template_name: state.reengagement_template_name || '',
      });
      setEngagementModal({ sub, state });
    } catch {
      setError('Erro ao carregar engajamento.');
    }
  }, []);

  const handleSaveEngagement = async () => {
    if (!engagementModal) return;
    setSavingEngagement(true);
    try {
      const payload = {};
      const v = engagementForm.max_consecutive_unanswered;
      if (v !== '' && v !== null && v !== undefined) {
        payload.max_consecutive_unanswered = parseInt(v, 10);
      }
      payload.reengagement_template_name = engagementForm.reengagement_template_name || '';
      const updated = await drSalusService.updateEngagement(engagementModal.sub.phone, payload);
      setEngagementModal(m => ({ ...m, state: updated }));
    } catch {
      setError('Erro ao salvar engajamento.');
    } finally {
      setSavingEngagement(false);
    }
  };

  const handleResumeReminders = async () => {
    if (!engagementModal) return;
    setSavingEngagement(true);
    try {
      const updated = await drSalusService.resumeReminders(engagementModal.sub.phone);
      setEngagementModal(m => ({ ...m, state: updated }));
    } catch {
      setError('Erro ao reativar lembretes.');
    } finally {
      setSavingEngagement(false);
    }
  };

  // ============================================
  // ACTIONS
  // ============================================

  const handleCreate = async () => {
    if (!form.plan_id || !form.subscriber_name || !form.subscriber_phone || !form.subscriber_cpf) return;
    setSaving(true);
    try {
      const result = await drSalusService.createSubscription(form);
      setCheckoutResult({ url: result.checkout_url, name: form.subscriber_name });
      setShowForm(false);
      setForm(EMPTY_FORM);
      await loadData();
    } catch {
      setError('Erro ao criar assinatura.');
    } finally {
      setSaving(false);
    }
  };

  const handleGrant = async () => {
    if (!grantModal) return;
    setSaving(true);
    try {
      await drSalusService.grantMonths(grantModal.sub.id, grantModal.months, grantModal.reason || 'Concessão manual');
      setGrantModal(null);
      await loadData();
    } catch {
      setError('Erro ao conceder meses.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscriber = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const payload = {
        name: editModal.name,
        email: editModal.email,
        cpf_cnpj: editModal.cpf_cnpj,
        phone: editModal.phone,
      };
      if (editModal.plan_id && editModal.plan_id !== editModal.original_plan_id) {
        payload.plan_id = editModal.plan_id;
      }
      await drSalusService.updateSubscription(editModal.id, payload);
      setEditModal(null);
      await loadData();
    } catch {
      setError('Erro ao atualizar assinante.');
    } finally {
      setSaving(false);
    }
  };

  const handleEnrollDesafio = async (sub) => {
    const ok = window.confirm(
      `Ativar o Desafio Meu Intestino Blindado para ${sub.name || sub.phone}?\n\n` +
      'Sem cobrança. A pessoa entra hoje (dia 1) e passa a receber os materiais diários pelo número do Desafio. ' +
      'Se ela nunca falou com esse número, o primeiro contato é o template de abertura e os materiais chegam quando ela responder.'
    );
    if (!ok) return;
    try {
      const res = await drSalusService.enrollDesafio(sub.phone);
      if (res.already_enrolled) setError(`${sub.phone} já estava no Desafio.`);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao ativar o Desafio.');
    }
  };

  const handleUnenrollDesafio = async (sub) => {
    if (!window.confirm(`Remover ${sub.name || sub.phone} do Desafio? Os materiais diários param de ser enviados.`)) return;
    try {
      await drSalusService.unenrollDesafio(sub.phone);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao remover do Desafio.');
    }
  };

  const handleToggleAccess = async (sub) => {
    try {
      await drSalusService.toggleAccess(sub.id, !sub.wa_access_enabled);
      await loadData();
    } catch {
      setError('Erro ao alterar acesso.');
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Excluir definitivamente o assinante ${sub.name || sub.phone}? Isso NÃO cancela a cobrança no Asaas.`)) return;
    try {
      await drSalusService.deleteSubscription(sub.id);
      await loadData();
    } catch {
      setError('Erro ao excluir assinante.');
    }
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ============================================
  // WHITELIST ACTIONS
  // ============================================

  const handleAddWhitelist = async () => {
    if (!whitelistForm.phone) return;
    setSavingWhitelist(true);
    try {
      await drSalusService.addToWhitelist(whitelistForm.phone, whitelistForm.reason);
      setWhitelistForm({ phone: '', reason: '' });
      setShowWhitelistForm(false);
      await loadWhitelist();
    } catch {
      setError('Erro ao adicionar ao whitelist.');
    } finally {
      setSavingWhitelist(false);
    }
  };

  const handleToggleWhitelist = async (entry) => {
    try {
      await drSalusService.toggleWhitelist(entry.id, !entry.active);
      await loadWhitelist();
    } catch {
      setError('Erro ao alterar status do whitelist.');
    }
  };

  const handleRemoveWhitelist = async (entry) => {
    if (!window.confirm(`Remover ${entry.phone} do whitelist?`)) return;
    try {
      await drSalusService.removeFromWhitelist(entry.id);
      await loadWhitelist();
    } catch {
      setError('Erro ao remover do whitelist.');
    }
  };

  // ============================================
  // FILTERS
  // ============================================

  const filtered = subscribers.filter(sub => {
    if (filter) {
      const f = filter.toLowerCase();
      const matchName = sub.name?.toLowerCase().includes(f);
      const matchPhone = sub.phone?.includes(filter);
      if (!matchName && !matchPhone) return false;
    }
    if (planFilter && sub.plan_id !== planFilter) return false;
    return true;
  });

  // ============================================
  // STYLES
  // ============================================

  const s = {
    page: { padding: '24px', maxWidth: '1200px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
    title: { fontSize: '20px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '10px' },
    btn: (variant = 'primary') => ({
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      backgroundColor: variant === 'primary' ? '#10b981' : variant === 'danger' ? '#ef444415' : colors.bgSecondary,
      color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#ef4444' : colors.textPrimary,
    }),
    tabsRow: {
      display: 'flex', gap: '4px', marginBottom: '20px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    tab: (active) => ({
      padding: '10px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      borderBottom: `2px solid ${active ? '#10b981' : 'transparent'}`,
      color: active ? '#10b981' : colors.textSecondary,
      background: 'transparent', border: 'none', borderRadius: 0,
      borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
    }),
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '12px', marginBottom: '20px',
    },
    statCard: { padding: '14px 16px', backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.borderColor}` },
    statLabel: { fontSize: '11px', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    statValue: { fontSize: '22px', fontWeight: 700, color: colors.textPrimary },
    filters: { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' },
    searchBox: {
      flex: 1, minWidth: '220px', display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
    },
    searchInput: { flex: 1, border: 'none', backgroundColor: 'transparent', color: colors.textPrimary, fontSize: '13px', outline: 'none' },
    select: {
      padding: '8px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '13px',
    },
    checkboxLabel: {
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: '12px', color: colors.textSecondary, cursor: 'pointer',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '10px 14px', fontSize: '11px', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${colors.borderColor}` },
    td: { padding: '12px 14px', fontSize: '13px', color: colors.textPrimary, borderBottom: `1px solid ${colors.borderColor}` },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalBox: { backgroundColor: colors.bgPrimary, borderRadius: '16px', padding: '28px', width: '480px', maxWidth: '95vw' },
    label: { fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '6px', display: 'block' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '14px', boxSizing: 'border-box' },
    formRow: { marginBottom: '14px' },
    iconBtn: { padding: '6px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: 'transparent', color: colors.textSecondary },
    actionMenu: {
      position: 'absolute', right: 0, top: '100%', marginTop: '4px',
      backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px', boxShadow: colors.shadowLg, padding: '6px',
      minWidth: '200px', zIndex: 50,
    },
    actionItem: {
      display: 'flex', alignItems: 'center', gap: '8px',
      width: '100%', padding: '8px 12px', border: 'none',
      backgroundColor: 'transparent', color: colors.textPrimary,
      fontSize: '13px', cursor: 'pointer', borderRadius: '6px', textAlign: 'left',
    },
  };

  // ============================================
  // RENDER
  // ============================================

  const renderStats = () => stats && (
    <div style={s.statsGrid}>
      <div style={s.statCard}><div style={s.statLabel}>Total</div><div style={s.statValue}>{stats.total ?? 0}</div></div>
      <div style={s.statCard}><div style={s.statLabel}>Ativos</div><div style={{ ...s.statValue, color: '#22c55e' }}>{stats.active ?? 0}</div></div>
      <div style={s.statCard}><div style={s.statLabel}>Inadimplentes</div><div style={{ ...s.statValue, color: '#f59e0b' }}>{stats.overdue ?? 0}</div></div>
      <div style={s.statCard}><div style={s.statLabel}>Pendentes</div><div style={{ ...s.statValue, color: '#3b82f6' }}>{stats.pending ?? 0}</div></div>
      <div style={s.statCard}><div style={s.statLabel}>Whitelist</div><div style={{ ...s.statValue, color: '#8b5cf6' }}>{stats.whitelist_count ?? 0}</div></div>
      <div style={s.statCard}><div style={s.statLabel}>Sem assinatura</div><div style={{ ...s.statValue, color: '#737373' }}>{stats.engagement_only ?? 0}</div></div>
    </div>
  );

  const renderSubscribersTab = () => (
    <>
      {renderStats()}

      {checkoutResult && (
        <div style={{ padding: '16px 20px', backgroundColor: '#10b98115', border: '1px solid #10b98150', borderRadius: '12px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#10b981', marginBottom: '6px' }}>
                <Check size={16} style={{ display: 'inline', marginRight: '6px' }} />
                Assinatura criada para {checkoutResult.name}
              </div>
              <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '8px' }}>Compartilhe o link de pagamento abaixo:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <code style={{ fontSize: '12px', backgroundColor: colors.bgSecondary, padding: '6px 10px', borderRadius: '6px', color: colors.textPrimary, wordBreak: 'break-all' }}>
                  {checkoutResult.url}
                </code>
                <button style={{ ...s.btn('secondary'), padding: '6px 10px' }} onClick={() => copyUrl(checkoutResult.url, 'result')}>
                  {copiedId === 'result' ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                </button>
                <a href={checkoutResult.url} target="_blank" rel="noreferrer" style={{ ...s.btn('secondary'), padding: '6px 10px', textDecoration: 'none' }}>
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
            <button style={s.iconBtn} onClick={() => setCheckoutResult(null)}><X size={18} /></button>
          </div>
        </div>
      )}

      <div style={s.filters}>
        <div style={s.searchBox}>
          <Search size={14} color={colors.textSecondary} />
          <input style={s.searchInput} placeholder="Buscar por nome ou telefone..." value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="overdue">Inadimplentes</option>
          <option value="pending">Pendentes</option>
          <option value="canceled">Cancelados</option>
          <option value="inactive">Inativos</option>
        </select>
        <select style={s.select} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">Todos os planos</option>
          {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <label style={s.checkboxLabel}>
          <input
            type="checkbox"
            checked={includeEngagementOnly}
            onChange={e => setIncludeEngagementOnly(e.target.checked)}
          />
          Incluir contatos sem assinatura
        </label>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={24} color="#10b981" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: colors.textSecondary }}>Nenhum assinante encontrado.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Nome</th>
              <th style={s.th}>Telefone</th>
              <th style={s.th}>Plano</th>
              <th style={s.th}>Valor</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Acesso</th>
              <th style={s.th}>Desafio</th>
              <th style={s.th}>Engajamento</th>
              <th style={s.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(sub => {
              const paused = sub.templates_paused_until && new Date(sub.templates_paused_until) > new Date();
              const unanswered = sub.consecutive_unanswered_templates || 0;
              const cap = sub.max_consecutive_unanswered ?? engagementSettings?.default_max_consecutive_unanswered ?? '?';
              const des = desafioMap[sub.phone];
              const noDesafio = des && (des.status === 'PARTICIPANDO' || des.status === 'CONCLUIDO');
              return (
                <tr key={sub.id}>
                  <td style={s.td}>{sub.name || '—'}</td>
                  <td style={s.td}>{sub.phone}</td>
                  <td style={s.td}>{sub.plan_name || sub.plan_id || '—'}</td>
                  <td style={s.td}>{sub.plan_value ? `R$ ${parseFloat(sub.plan_value).toFixed(2)}/${sub.plan_cycle === 'MONTHLY' ? 'mês' : sub.plan_cycle}` : '—'}</td>
                  <td style={s.td}><StatusBadge status={sub.plan_status} /></td>
                  <td style={s.td}>
                    {sub.wa_access_enabled ? (
                      <span style={{ color: '#22c55e', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                        <ShieldCheck size={14} /> Liberado
                      </span>
                    ) : (
                      <span style={{ color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                        <ShieldOff size={14} /> Bloqueado
                      </span>
                    )}
                  </td>
                  <td style={s.td}>
                    {noDesafio ? (
                      <span
                        title={`${des.followup_name || 'Desafio'} · desde ${des.enrolled_at || '?'}${sub.desafio_source ? ` · origem: ${sub.desafio_source}` : ''}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', backgroundColor: des.status === 'CONCLUIDO' ? '#a3a3a320' : '#f9731620', color: des.status === 'CONCLUIDO' ? '#737373' : '#f97316' }}
                      >
                        {des.status === 'CONCLUIDO' ? <Trophy size={12} /> : <Flame size={12} />}
                        {des.status === 'CONCLUIDO' ? 'Concluído' : `Dia ${des.dia_atual ?? '?'}/${des.total_days ?? '?'}`}
                      </span>
                    ) : (
                      <span style={{ color: colors.textSecondary, fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={s.td}>
                    <button
                      style={{
                        ...s.btn('secondary'),
                        padding: '4px 10px', fontSize: '11px',
                        backgroundColor: paused ? '#f59e0b20' : (unanswered > 0 ? '#3b82f620' : colors.bgSecondary),
                        color: paused ? '#f59e0b' : (unanswered > 0 ? '#3b82f6' : colors.textPrimary),
                      }}
                      onClick={() => openEngagementModal(sub)}
                      title={paused ? `Pausado até ${new Date(sub.templates_paused_until).toLocaleString('pt-BR')}` : `${unanswered}/${cap} templates sem resposta`}
                    >
                      {paused ? <BellOff size={12} /> : <Bell size={12} />}
                      {paused ? 'Pausado' : `${unanswered}/${cap}`}
                    </button>
                  </td>
                  <td style={{ ...s.td, position: 'relative' }}>
                    <button
                      className="row-action-trigger"
                      style={s.iconBtn}
                      onClick={() => setOpenMenuId(openMenuId === sub.id ? null : sub.id)}
                    >
                      <MoreHorizontal size={18} />
                    </button>
                    {openMenuId === sub.id && (
                      <div className="row-action-menu" style={s.actionMenu}>
                        <button style={s.actionItem} onClick={() => {
                          setEditModal({
                            id: sub.id,
                            name: sub.name || '',
                            email: sub.email || '',
                            cpf_cnpj: sub.cpf_cnpj || '',
                            phone: sub.phone || '',
                            plan_id: sub.plan_id || '',
                            original_plan_id: sub.plan_id || '',
                          });
                          setOpenMenuId(null);
                        }}>
                          <Edit2 size={14} /> Editar
                        </button>
                        <button style={s.actionItem} onClick={() => {
                          setGrantModal({ sub, months: 1, reason: '' });
                          setOpenMenuId(null);
                        }}>
                          <CalendarPlus size={14} /> Conceder N meses
                        </button>
                        {sub.checkout_url && sub.plan_status === 'pending' && (
                          <button style={s.actionItem} onClick={() => {
                            copyUrl(sub.checkout_url, sub.id);
                            setOpenMenuId(null);
                          }}>
                            <Copy size={14} /> Copiar link checkout
                          </button>
                        )}
                        <button style={s.actionItem} onClick={() => {
                          handleToggleAccess(sub);
                          setOpenMenuId(null);
                        }}>
                          {sub.wa_access_enabled ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                          {sub.wa_access_enabled ? 'Bloquear acesso' : 'Liberar acesso'}
                        </button>
                        {noDesafio ? (
                          <button style={s.actionItem} onClick={() => {
                            handleUnenrollDesafio(sub);
                            setOpenMenuId(null);
                          }}>
                            <Flame size={14} /> Remover do Desafio
                          </button>
                        ) : (
                          <button style={s.actionItem} onClick={() => {
                            handleEnrollDesafio(sub);
                            setOpenMenuId(null);
                          }}>
                            <Flame size={14} /> Ativar no Desafio
                          </button>
                        )}
                        <div style={{ height: 1, background: colors.borderColor, margin: '4px 0' }} />
                        <button style={{ ...s.actionItem, color: '#ef4444' }} onClick={() => {
                          handleDelete(sub);
                          setOpenMenuId(null);
                        }}>
                          <Trash2 size={14} /> Excluir
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );

  const renderWhitelistTab = () => (
    <>
      <div style={{ padding: '12px 16px', backgroundColor: '#8b5cf615', border: '1px solid #8b5cf640', borderRadius: '10px', marginBottom: '20px', fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5 }}>
        <strong style={{ color: '#8b5cf6' }}>Whitelist Dr. Salus:</strong> números aqui sempre passam o gating do agente — mesmo sem assinatura ativa. Use para testers internos. Não interfere com o Projeto 1 (Desafios).
      </div>

      {loadingWhitelist ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={24} color="#8b5cf6" />
        </div>
      ) : whitelist.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: colors.textSecondary }}>Nenhum número no whitelist.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Telefone</th>
              <th style={s.th}>Motivo</th>
              <th style={s.th}>Criado em</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {whitelist.map(entry => (
              <tr key={entry.id}>
                <td style={s.td}>{entry.phone}</td>
                <td style={{ ...s.td, color: colors.textSecondary, fontSize: '12px' }}>{entry.reason || '—'}</td>
                <td style={{ ...s.td, color: colors.textSecondary, fontSize: '12px' }}>
                  {entry.created_at ? new Date(entry.created_at).toLocaleString('pt-BR') : '—'}
                </td>
                <td style={s.td}>
                  {entry.active ? (
                    <span style={{ color: '#22c55e', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                      <CheckCircle size={14} /> Ativo
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                      <XCircle size={14} /> Inativo
                    </span>
                  )}
                </td>
                <td style={s.td}>
                  <button
                    style={{ ...s.btn('secondary'), padding: '4px 10px', fontSize: '12px', marginRight: '6px' }}
                    onClick={() => handleToggleWhitelist(entry)}
                  >
                    {entry.active ? 'Desativar' : 'Ativar'}
                  </button>
                  <button
                    style={{ ...s.btn('danger'), padding: '4px 10px', fontSize: '12px' }}
                    onClick={() => handleRemoveWhitelist(entry)}
                  >
                    <Trash2 size={12} /> Remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          <Users size={22} color="#10b981" />
          Assinantes Dr. Salus
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.btn('secondary')} onClick={activeTab === 'subscribers' ? loadData : loadWhitelist}>
            <RefreshCw size={14} /> Atualizar
          </button>
          {activeTab === 'subscribers' ? (
            <button style={s.btn('primary')} onClick={() => setShowForm(true)}>
              <Plus size={14} /> Gerar Assinatura
            </button>
          ) : (
            <button style={{ ...s.btn('primary'), backgroundColor: '#8b5cf6' }} onClick={() => setShowWhitelistForm(true)}>
              <Plus size={14} /> Adicionar Número
            </button>
          )}
        </div>
      </div>

      <div style={s.tabsRow}>
        <button style={s.tab(activeTab === 'subscribers')} onClick={() => setActiveTab('subscribers')}>
          <Users size={13} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
          Assinantes
        </button>
        <button style={s.tab(activeTab === 'whitelist')} onClick={() => setActiveTab('whitelist')}>
          <ListChecks size={13} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
          Whitelist (Testes)
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 16px', backgroundColor: '#ef444420', color: '#ef4444', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} /> {error}
          <button style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {activeTab === 'subscribers' ? renderSubscribersTab() : renderWhitelistTab()}

      {/* Modal: Gerar Assinatura */}
      {showForm && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>Gerar Link de Assinatura</h2>
              <button style={s.iconBtn} onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Plano *</label>
              <select style={{ ...s.input, cursor: 'pointer' }} value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
                <option value="">Selecione um plano</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} — R$ {parseFloat(p.value).toFixed(2)}/{p.cycle}</option>)}
              </select>
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Nome completo *</label>
              <input style={s.input} value={form.subscriber_name} onChange={e => setForm(f => ({ ...f, subscriber_name: e.target.value }))} placeholder="João Silva" />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Telefone (com DDD) *</label>
              <input style={s.input} value={form.subscriber_phone} onChange={e => setForm(f => ({ ...f, subscriber_phone: e.target.value }))} placeholder="5511999999999" />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>CPF *</label>
              <input style={s.input} value={form.subscriber_cpf} onChange={e => setForm(f => ({ ...f, subscriber_cpf: e.target.value }))} placeholder="000.000.000-00" />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>E-mail</label>
              <input style={s.input} type="email" value={form.subscriber_email} onChange={e => setForm(f => ({ ...f, subscriber_email: e.target.value }))} placeholder="joao@email.com (opcional)" />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btn('secondary')} onClick={() => setShowForm(false)} disabled={saving}>Cancelar</button>
              <button style={s.btn('primary')} onClick={handleCreate} disabled={saving || !form.plan_id || !form.subscriber_name || !form.subscriber_phone || !form.subscriber_cpf}>
                {saving ? <Loader2 size={14} /> : <Check size={14} />}
                Gerar Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar Assinante */}
      {editModal && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>Editar Assinante</h2>
              <button style={s.iconBtn} onClick={() => setEditModal(null)}><X size={18} /></button>
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Nome</label>
              <input style={s.input} value={editModal.name} onChange={e => setEditModal(m => ({ ...m, name: e.target.value }))} />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Telefone</label>
              <input style={s.input} value={editModal.phone} onChange={e => setEditModal(m => ({ ...m, phone: e.target.value }))} />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>CPF</label>
              <input style={s.input} value={editModal.cpf_cnpj} onChange={e => setEditModal(m => ({ ...m, cpf_cnpj: e.target.value }))} />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>E-mail</label>
              <input style={s.input} type="email" value={editModal.email} onChange={e => setEditModal(m => ({ ...m, email: e.target.value }))} />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Plano</label>
              <select style={{ ...s.input, cursor: 'pointer' }} value={editModal.plan_id} onChange={e => setEditModal(m => ({ ...m, plan_id: e.target.value }))}>
                <option value="">— Sem plano —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} — R$ {parseFloat(p.value).toFixed(2)}/{p.cycle}</option>)}
              </select>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '6px' }}>
                Trocar o plano NÃO altera a cobrança no Asaas. Cancele/recrie a assinatura no Asaas se necessário.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btn('secondary')} onClick={() => setEditModal(null)} disabled={saving}>Cancelar</button>
              <button style={s.btn('primary')} onClick={handleUpdateSubscriber} disabled={saving}>
                {saving ? <Loader2 size={14} /> : <Check size={14} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Conceder N meses */}
      {grantModal && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CalendarPlus size={18} color="#10b981" />
                Conceder Plano Manualmente
              </h2>
              <button style={s.iconBtn} onClick={() => setGrantModal(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '12px 16px', backgroundColor: '#f59e0b15', border: '1px solid #f59e0b40', borderRadius: '10px', marginBottom: '18px', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
              <strong style={{ color: '#f59e0b' }}>Atenção:</strong> isso libera acesso ao agente para <strong>{grantModal.sub.name || grantModal.sub.phone}</strong> pelos próximos N meses <strong>SEM</strong> cobrança no Asaas. Use para cortesias ou recuperação de erro de webhook.
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Quantos meses</label>
              <input
                style={s.input}
                type="number"
                min="1"
                max="24"
                value={grantModal.months}
                onChange={e => setGrantModal(m => ({ ...m, months: parseInt(e.target.value || '1', 10) }))}
              />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Motivo</label>
              <input
                style={s.input}
                value={grantModal.reason}
                onChange={e => setGrantModal(m => ({ ...m, reason: e.target.value }))}
                placeholder="Ex: Cortesia / Erro de webhook / Tester"
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btn('secondary')} onClick={() => setGrantModal(null)} disabled={saving}>Cancelar</button>
              <button style={s.btn('primary')} onClick={handleGrant} disabled={saving || !grantModal.months}>
                {saving ? <Loader2 size={14} /> : <Check size={14} />}
                Conceder {grantModal.months} {grantModal.months === 1 ? 'mês' : 'meses'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Adicionar ao Whitelist */}
      {showWhitelistForm && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>Adicionar Número ao Whitelist</h2>
              <button style={s.iconBtn} onClick={() => setShowWhitelistForm(false)}><X size={18} /></button>
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Telefone (com DDD) *</label>
              <input
                style={s.input}
                value={whitelistForm.phone}
                onChange={e => setWhitelistForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="5511999999999"
              />
            </div>
            <div style={s.formRow}>
              <label style={s.label}>Motivo</label>
              <input
                style={s.input}
                value={whitelistForm.reason}
                onChange={e => setWhitelistForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Ex: Tester interno"
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btn('secondary')} onClick={() => setShowWhitelistForm(false)} disabled={savingWhitelist}>Cancelar</button>
              <button style={{ ...s.btn('primary'), backgroundColor: '#8b5cf6' }} onClick={handleAddWhitelist} disabled={savingWhitelist || !whitelistForm.phone}>
                {savingWhitelist ? <Loader2 size={14} /> : <Check size={14} />}
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Engagement (existente — intocado) */}
      {engagementModal && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bell size={16} color="#10b981" />
                Engajamento — {engagementModal.sub.name || engagementModal.sub.phone}
              </h2>
              <button style={s.iconBtn} onClick={() => setEngagementModal(null)}><X size={18} /></button>
            </div>

            <div style={{ padding: '12px 16px', backgroundColor: colors.bgSecondary, borderRadius: '10px', marginBottom: '18px', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.6 }}>
              <div><strong style={{ color: colors.textPrimary }}>Estado atual:</strong></div>
              <div>Sem resposta: <strong style={{ color: colors.textPrimary }}>{engagementModal.state.consecutive_unanswered_templates ?? 0}</strong> / {engagementModal.state.max_consecutive_unanswered}</div>
              {engagementModal.state.last_template_at && (
                <div>Último template: {new Date(engagementModal.state.last_template_at).toLocaleString('pt-BR')}</div>
              )}
              {engagementModal.state.last_user_reply_at && (
                <div>Última resposta do usuário: {new Date(engagementModal.state.last_user_reply_at).toLocaleString('pt-BR')}</div>
              )}
              {engagementModal.state.is_paused ? (
                <div style={{ color: '#f59e0b', marginTop: '4px' }}>
                  <BellOff size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Pausado até {new Date(engagementModal.state.templates_paused_until).toLocaleString('pt-BR')}
                </div>
              ) : (
                <div style={{ color: '#10b981', marginTop: '4px' }}>
                  <CheckCircle size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Ativo
                </div>
              )}
            </div>

            <div style={s.formRow}>
              <label style={s.label}>
                Máximo de templates sem resposta antes de pausar
                {engagementSettings && <span style={{ color: colors.textSecondary, fontWeight: 400 }}> (default global: {engagementSettings.default_max_consecutive_unanswered})</span>}
              </label>
              <input
                style={s.input}
                type="number"
                min="1"
                max="100"
                placeholder={`${engagementSettings?.default_max_consecutive_unanswered ?? 5} (deixe vazio p/ usar default)`}
                value={engagementForm.max_consecutive_unanswered}
                onChange={e => setEngagementForm(f => ({ ...f, max_consecutive_unanswered: e.target.value }))}
              />
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Template de reengajamento (enviado UMA vez ao bater o cap)</label>
              <select
                style={{ ...s.input, cursor: 'pointer' }}
                value={engagementForm.reengagement_template_name}
                onChange={e => setEngagementForm(f => ({ ...f, reengagement_template_name: e.target.value }))}
              >
                <option value="">— Nenhum (apenas pausar) —</option>
                {approvedTemplates.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
              {engagementSettings && (
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '6px' }}>
                  Pausa de {engagementSettings.default_pause_days} dias após o cap. Resposta do usuário reativa automaticamente.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '16px' }}>
              <button
                style={{ ...s.btn('secondary'), color: '#10b981' }}
                disabled={savingEngagement || !engagementModal.state.is_paused}
                onClick={handleResumeReminders}
              >
                <Bell size={14} /> Reativar lembretes agora
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={s.btn('secondary')} onClick={() => setEngagementModal(null)} disabled={savingEngagement}>Fechar</button>
                <button style={s.btn('primary')} onClick={handleSaveEngagement} disabled={savingEngagement}>
                  {savingEngagement ? <Loader2 size={14} /> : <Check size={14} />}
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
